import React, { useEffect } from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { FaTractor } from 'react-icons/fa';
import { t } from 'ttag';
import { SCOPES } from '../../constants/scopes';
import useCropTypes, { CROP_TYPE_CODES } from '../../hooks/useCropTypes';
import usePreCropTypes, { PRE_CROP_CODES } from '../../hooks/usePreCropTypes';
import useSoilTypes, { SOIL_TYPE_CODES } from '../../hooks/useSoilTypes';
import { parseFullYearFromScope } from '../../utils/utils';
import {
  FILTERS,
  getSeedDateTypeByCode,
  SEED_DATE_TYPES,
  COVER_CROP_TYPES,
  getCoverCropTypeByCode,
  SEED_DATE_CODES,
  HARVEST_DATE_CODES,
  COVER_CROP_CODES,
} from '../Field/constants/filters';
import { FieldSearchWrap } from '../Fields/fields.styles';
import FieldLabel from '../Shared/FieldLabel/FieldLabel';
import Filter from '../Shared/Filter/Filter';
import useFilters from '../Shared/Filter/useFilters';
import RadioButton from '../Shared/RadioButton/RadioButton';

export default function Filters({ getSearchParams, onSavedFiltersUpdate }) {
  const {
    handleFilterChange,
    handleFilterToggleClick,
    handleFilterCloseClick,
    handleFilterSaveClick,
    activeFilter,
    savedFilters,
    unsavedFilters,
    setUnsavedAndSavedFilter,
  } = useFilters(getFilters());

  useEffect(() => {
    onSavedFiltersUpdate(savedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  function getFilters() {
    const { cropType, year, mapLayer, soilType, seedDate, harvestDate, coverCrop, preCrop } =
      getSearchParams();
    return {
      cropType,
      year,
      mapLayer,
      soilType,
      seedDate,
      harvestDate,
      coverCrop,
      preCrop,
    };
  }

  const { soilTypes, getSoilTypeNameByCode } = useSoilTypes(savedFilters.year);
  const { cropTypes, getCropTypeNameByCode } = useCropTypes(savedFilters.year);
  const { preCropTypes } = usePreCropTypes(savedFilters.year);

  function resetFilters() {
    setUnsavedAndSavedFilter(FILTERS.year, SCOPES[SCOPES.length - 1].value);
    setUnsavedAndSavedFilter(FILTERS.cropType, CROP_TYPE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.soilType, SOIL_TYPE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.seedDate, SEED_DATE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.harvestDate, HARVEST_DATE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.coverCrop, COVER_CROP_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.preCrop, PRE_CROP_CODES.ALL);
  }

  return (
    <FieldSearchWrap>
      <div className="field-filters-wrap">
        <div className="field-information-stats">
          <div className="field-information">
            <FieldLabel
              icon={<AiFillCalendar />}
              value={parseFullYearFromScope(savedFilters.year)}
              label={t`Year`}
            />
            <FieldLabel
              icon={<FaTractor />}
              label={t`Crop type`}
              value={getCropTypeNameByCode(savedFilters.cropType)}
            />
            <FieldLabel label={t`Soil type`} value={getSoilTypeNameByCode(savedFilters.soilType)} />
            <FieldLabel label={t`Cover crop`} value={getCoverCropTypeByCode(savedFilters.coverCrop)} />
            <FieldLabel label={t`Pre crop`} value={getCropTypeNameByCode(savedFilters.preCrop)} />
            <FieldLabel label={t`Seed date`} value={getSeedDateTypeByCode(savedFilters.seedDate)} />
            <span className="reset" onClick={resetFilters}>{t`Reset filters`}</span>
          </div>
        </div>
        <div className="field-filters">
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.year)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.year)}
            onToggleClick={() => {
              handleFilterToggleClick(FILTERS.year);
            }}
            onCloseClick={() => handleFilterCloseClick(FILTERS.year)}
            isOpen={activeFilter === FILTERS.year}
            label={t`Year`}
          >
            {SCOPES.map(({ year, value }) => (
              <RadioButton
                key={value}
                onChange={(event) => {
                  handleFilterChange(FILTERS.year, event.currentTarget.value);
                }}
                label={year}
                value={value}
                checked={unsavedFilters.year === value}
              />
            ))}
          </Filter>
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.cropType)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.cropType)}
            onToggleClick={() => handleFilterToggleClick(FILTERS.cropType)}
            onCloseClick={() => handleFilterCloseClick(FILTERS.cropType)}
            isOpen={activeFilter === FILTERS.cropType}
            label={t`Crop type`}
          >
            {cropTypes.map((crop) => (
              <RadioButton
                key={crop.code}
                onChange={(event) => {
                  handleFilterChange(FILTERS.cropType, event.currentTarget.value);
                }}
                value={crop.code}
                checked={unsavedFilters.cropType === crop.code}
                label={crop.names.de.toLowerCase()}
              />
            ))}
          </Filter>
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.soilType)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.soilType)}
            onToggleClick={() => handleFilterToggleClick(FILTERS.soilType)}
            onCloseClick={() => handleFilterCloseClick(FILTERS.soilType)}
            isOpen={activeFilter === FILTERS.soilType}
            label={t`Soil type`}
          >
            {soilTypes.map((soil) => (
              <RadioButton
                key={soil.code}
                onChange={(event) => {
                  handleFilterChange(FILTERS.soilType, event.currentTarget.value);
                }}
                value={soil.code}
                checked={unsavedFilters.soilType === soil.code}
                label={soil.names.de.toLowerCase()}
              />
            ))}
          </Filter>
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.coverCrop)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.coverCrop)}
            onToggleClick={() => handleFilterToggleClick(FILTERS.coverCrop)}
            onCloseClick={() => handleFilterCloseClick(FILTERS.coverCrop)}
            isOpen={activeFilter === FILTERS.coverCrop}
            label={t`Cover crop`}
          >
            {COVER_CROP_TYPES.map((coverCrop) => (
              <RadioButton
                key={coverCrop.code}
                onChange={(event) => {
                  handleFilterChange(FILTERS.coverCrop, event.currentTarget.value);
                }}
                value={coverCrop.code}
                checked={unsavedFilters.coverCrop === coverCrop.code}
                label={coverCrop.names.de.toLowerCase()}
              />
            ))}
          </Filter>
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.preCrop)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.preCrop)}
            onToggleClick={() => handleFilterToggleClick(FILTERS.preCrop)}
            onCloseClick={() => handleFilterCloseClick(FILTERS.preCrop)}
            isOpen={activeFilter === FILTERS.preCrop}
            label={t`Pre crop`}
          >
            {preCropTypes.map((crop) => (
              <RadioButton
                key={crop.code}
                onChange={(event) => {
                  handleFilterChange(FILTERS.preCrop, event.currentTarget.value);
                }}
                value={crop.code}
                checked={unsavedFilters.preCrop === crop.code}
                label={crop.names.de.toLowerCase()}
              />
            ))}
          </Filter>
          <Filter
            popupPosition="top"
            size="small"
            onOutsideClick={() => handleFilterCloseClick(FILTERS.seedDate)}
            onSaveChangesClick={() => handleFilterSaveClick(FILTERS.seedDate)}
            onToggleClick={() => handleFilterToggleClick(FILTERS.seedDate)}
            onCloseClick={() => handleFilterCloseClick(FILTERS.seedDate)}
            isOpen={activeFilter === FILTERS.seedDate}
            label={t`Seed date`}
          >
            {SEED_DATE_TYPES.map((seedDate) => (
              <RadioButton
                key={seedDate.code}
                onChange={(event) => {
                  handleFilterChange(FILTERS.seedDate, event.currentTarget.value);
                }}
                value={seedDate.code}
                checked={unsavedFilters.seedDate === seedDate.code}
                label={seedDate.names.de.toLowerCase()}
              />
            ))}
          </Filter>
        </div>
      </div>
    </FieldSearchWrap>
  );
}
